/**
 * The access token lives here — in a module-scoped variable, never in
 * `localStorage` or `sessionStorage`.
 *
 * Anything in web storage is readable by any script on the page, so a single
 * XSS payload would exfiltrate a token that keeps working until it expires.
 * Held in memory it dies with the tab, and a hard reload re-establishes the
 * session from the httpOnly refresh cookie instead (see `AuthProvider`).
 *
 * The cost is that a page reload always costs one `/auth/refresh` round-trip.
 * That is the right trade.
 */
let accessToken: string | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) listener();
}

export function clearAccessToken(): void {
  setAccessToken(null);
}

/** Notifies on every change — used to tear down cached data on sign-out. */
export function onAccessTokenChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
